import { Collapse } from '@mui/material';
import { OptionallyVisible } from 'components';
import { setIn, useFormik } from 'formik';
import { omitBy } from 'lodash';
import { useNotifications } from 'providers/notifications/useNotifications';
import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetRefundReserveQuery } from 'services/merchants';
import pwcOrdersApi from 'services/pwcOrders';
import { PwcRefundQueryPayload } from 'services/pwcOrders/types';
import * as Yup from 'yup';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { Delimiter } from 'components/delimiter';
import { FormField } from 'components/form/formField/formField';
import { ErrorMessage } from 'components/form/formField/styles';
import IconAdd from 'components/icons/add';
import IconCross from 'components/icons/cross';
import { Input, InputType } from 'components/inputs';
import { Checkbox } from 'components/inputs/checkbox/checkbox';
import { Select, SelectItem } from 'components/inputs/select/select';
import { SelectableItem } from 'components/inputs/select/styles';
import { Buttons, PanelTitle } from 'components/modal/styles';

import { TopupModalContent } from 'pages/merchant/edit/sections/refundAccount/components/topupModal/content';
import { OrderPwcData } from 'pages/pwcOrders/view/types';

import { isInsufficientRefundFundsError } from 'types/error';
import { ReduxToolkitError } from 'types/http';
import { NotificationSeverity } from 'types/notifications';
import { OrderItem as ItemOfOrder } from 'types/pwcOrder';

import { flattenKeys, getServerSideValidationDetails, unflattenErrors } from 'utils/error';
import { formatCurrencySymbol, formatNumberI18n } from 'utils/i18n';
import {
  areCurrencyCodesEqual,
  calculateRefundSettlementEstimate,
  getRefundEstimateAvailability,
  isRefundSettlementBelowMinimum,
} from 'utils/refundFx';

import ConfirmationDialog from './confirmation';
import CustomItemForm from './customItemForm';
import { AvailableRefundDuration, KEYS, LABELS, PLAIN_LABELS, RefundFormField, RefundFormValues } from './keys';
import OrderItem from './orderItem';
import {
  CloseButton,
  CurrencyAdornment,
  DurationControls,
  ExpandSectionHandle,
  ExpandSectionIcon,
  ItemButton,
  Label,
  ModalContent,
  ModalSection,
  OutstandingValue,
  SelectedSelectableItem,
  StyledDialog,
  Value,
} from './styles';

interface RefundModalProps {
  isEmailEditable: boolean;
  order: OrderPwcData;
  isOpen: boolean;
  toggle: () => void;
}

const RefundModal = ({ isEmailEditable, order, isOpen, toggle }: RefundModalProps) => {
  const { t, i18n } = useTranslation();
  const [customItems, setCustomItems] = useState<ItemOfOrder[]>([]);
  const [isAddingCustomItem, setIsAddingCustomItem] = useState(false);
  const [isAdvancedSettingsExpanded, setIsAdvancedSettingsExpanded] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isInsufficientBalance, setIsInsuficcientBalance] = useState(false);
  const [insufficientBalanceMessage, setInsufficientBalanceMessage] = useState<string>();
  const [refund, { error, isLoading, reset }] = pwcOrdersApi.useRefundMutation();
  const {
    data: refundReserve,
    isLoading: isRefundReserveLoading,
    isError: isRefundReserveError,
  } = useGetRefundReserveQuery(order.merchant.uuid, {
    skip: !isOpen || !order.merchant.uuid,
    refetchOnMountOrArgChange: true,
  });
  const { pushToast } = useNotifications();
  const isUsdOrder = areCurrencyCodesEqual(order.general.fiatCurrencyCode, 'USD');
  const estimateAvailability = getRefundEstimateAvailability({
    orderFiatCurrencyCode: order.general.fiatCurrencyCode,
    reserveFiatCurrencyCode: refundReserve?.fiatCurrencyCode,
    settlementCurrencyCode: refundReserve?.settlementCurrencyCode,
    displayFxRate: refundReserve?.displayFxRate,
    isLoading: isRefundReserveLoading,
    isError: isRefundReserveError,
  });

  const form = useFormik<RefundFormValues>({
    initialValues: {
      [KEYS.FORM_FIELDS.ITEMS]: [],
      [KEYS.FORM_FIELDS.FIAT_AMOUNT]: undefined,
      [KEYS.FORM_FIELDS.EMAIL]: order.customer.email ?? undefined,
      [KEYS.FORM_FIELDS.REFUND_TTL]: undefined,
      [KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]: undefined,
      [KEYS.FORM_FIELDS.REDIRECT_URL]: undefined,
      [KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]: undefined,
      [KEYS.FORM_FIELDS.WEBHOOK_URL]: undefined,
    },
    validationSchema: Yup.object().shape({
      [KEYS.FORM_FIELDS.FIAT_AMOUNT]: Yup.number()
        .min(
          isUsdOrder ? 1 : 0.01,
          isUsdOrder
            ? t(LABELS.ERRORS.MIN_AMOUNT, { currencyCode: order.general.fiatCurrencyCode })
            : t(LABELS.ERRORS.MIN_AMOUNT_POSITIVE),
        )
        .test('min-settlement', t(LABELS.ERRORS.MIN_SETTLEMENT), (value) => {
          if (isUsdOrder || value == null || estimateAvailability.isLoading) {
            return true;
          }

          const settlementEstimate = calculateRefundSettlementEstimate(
            value,
            estimateAvailability.displayFxRate,
          );
          if (settlementEstimate == null) {
            return true;
          }

          return !isRefundSettlementBelowMinimum(settlementEstimate);
        })
        .max(Number(order.general.paidFiatAmount) - order.general.refundedAmount, t(LABELS.ERRORS.MAX_AMOUNT))
        .required(t(LABELS.ERRORS.REQUIRED)),
      [KEYS.FORM_FIELDS.EMAIL]: isEmailEditable
        ? Yup.string().email(t(LABELS.ERRORS.EMAIL)).required(t(LABELS.ERRORS.REQUIRED))
        : Yup.string().email(t(LABELS.ERRORS.EMAIL)).optional(),
      [KEYS.FORM_FIELDS.REFUND_TTL]: Yup.number().optional(),
      [KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]: Yup.string().optional(),
      [KEYS.FORM_FIELDS.REDIRECT_URL]: Yup.string().url(t(LABELS.ERRORS.URL)).optional(),
      [KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]: Yup.string().url(t(LABELS.ERRORS.URL)).optional(),
      [KEYS.FORM_FIELDS.WEBHOOK_URL]: Yup.string().url(t(LABELS.ERRORS.URL)).optional(),
    }),
    onSubmit: async (values) => {
      if (!values[KEYS.FORM_FIELDS.FIAT_AMOUNT]) {
        throw new Error('No fiat amount provided for refund');
      }

      const initialPayload: PwcRefundQueryPayload = {
        ...values,
        orderUuid: order.general.uuid,
        fiatAmount: Number(values[KEYS.FORM_FIELDS.FIAT_AMOUNT]),
        [KEYS.FORM_FIELDS.ITEMS]: values[KEYS.FORM_FIELDS.ITEMS].map((item) => ({
          name: item.name,
          fiatAmount: Number(item.fiatAmount),
          type: item.type,
          ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
        })),
        ...(Boolean(values[KEYS.FORM_FIELDS.REFUND_TTL]) && {
          [KEYS.FORM_FIELDS.REFUND_TTL]: getCalculatedDuration(
            selectedDuration.key as AvailableRefundDuration,
            values[KEYS.FORM_FIELDS.REFUND_TTL]!,
          ),
        }),
      };

      const payload = omitBy(initialPayload, (value) => value === undefined || value === '') as PwcRefundQueryPayload;

      const response = await refund(payload);
      if ('error' in response) {
        const refundError = response.error as ReduxToolkitError;
        if (isInsufficientRefundFundsError(refundError?.data?.errorType)) {
          setInsufficientBalanceMessage(refundError?.data?.errorMessage);
          setIsInsuficcientBalance(true);
          setIsConfirming(false);
          return;
        }
        toggleConfirmationModal();
        const serverErrors = getServerSideValidationDetails(
          response.error,
          KEYS.FORM_FIELDS.FIAT_AMOUNT,
        );
        const touchedFields = flattenKeys(serverErrors).reduce((acc, path) => setIn(acc, path, true), {});

        form.setErrors(unflattenErrors(serverErrors));
        form.setTouched(touchedFields, false);

        return;
      }

      pushToast({
        severity: NotificationSeverity.success,
        message: t(LABELS.MESSAGES.CREATION_SUCCESS),
      });

      toggle();
    },
  });

  const durationSelectItems: SelectItem[] = Object.values(AvailableRefundDuration).map((duration) => ({
    key: duration.toString(),
    label: t(LABELS.DURATION[duration]),
  }));

  const [selectedDuration, setSelectedDuration] = useState<SelectItem>(durationSelectItems[0]);

  if (!order.general.fiatAmount || !order.general.fiatCurrencyCode) {
    throw new Error('Order does not have a fiat amount or code');
  }
  const formattedSymbol = formatCurrencySymbol(i18n.language)(order.general.fiatCurrencyCode);
  const numberFormatter = formatNumberI18n(i18n.language);
  const formattedAmount = numberFormatter(Number(order.general.paidFiatAmount) - order.general.refundedAmount);
  const formattedPlaceholder = numberFormatter(0);

  useEffect(() => {
    if (!isOpen) {
      setIsConfirming(false);
      setIsInsuficcientBalance(false);
      setInsufficientBalanceMessage(undefined);
      form.resetForm();
      reset();
    }
  }, [isOpen]);

  const onFormChange = (field: RefundFormField) => (value: string | number) => {
    form.setFieldTouched(field, true);
    form.setFieldValue(field, value);
  };

  const getCalculatedDuration = (durationPreset: AvailableRefundDuration, amount: number) =>
    ({
      [AvailableRefundDuration.Months]: Number(amount * 30 * 24 * 60),
      [AvailableRefundDuration.Days]: Number(amount * 24 * 60),
      [AvailableRefundDuration.Hours]: Number(amount * 60),
    })[durationPreset];

  const onDurationSelect = (item: SelectItem) => {
    setSelectedDuration(item);
  };

  const onItemToggle = (item: ItemOfOrder) => () => {
    const currentItems = form.values[KEYS.FORM_FIELDS.ITEMS];
    const containsItem = currentItems.some(({ uuid }) => uuid === item.uuid);
    if (containsItem) {
      form.setFieldValue(
        KEYS.FORM_FIELDS.ITEMS,
        currentItems.filter(({ uuid }) => uuid !== item.uuid),
      );
      return;
    }

    form.setFieldValue(KEYS.FORM_FIELDS.ITEMS, [...currentItems, item]);
  };

  const isItemSelected = (itemUuid: string) => {
    return form.values[KEYS.FORM_FIELDS.ITEMS].some(({ uuid }) => uuid === itemUuid);
  };

  const toggleAllItems = () => {
    const allItems = order.general.items?.concat(customItems) || [];
    const selectedItems = form.values[KEYS.FORM_FIELDS.ITEMS];

    if (allItems.length === selectedItems.length && allItems.every((item) => isItemSelected(item.uuid))) {
      form.setFieldValue(KEYS.FORM_FIELDS.ITEMS, []);
    } else {
      form.setFieldValue(KEYS.FORM_FIELDS.ITEMS, allItems);
    }
  };

  const areAllItemsToggled = () => {
    const allItems = order.general.items?.concat(customItems) || [];
    return allItems.length > 0 && allItems.every((item) => isItemSelected(item.uuid));
  };

  const toggleAllItemsLabel = areAllItemsToggled() ? t(LABELS.ACTIONS.DESELECT_ALL) : t(LABELS.ACTIONS.SELECT_ALL);

  const toggleConfirmationModal = () => {
    setIsConfirming(!isConfirming);
  };

  const onProceedClick = async () => {
    if (estimateAvailability.isLoading) {
      return;
    }

    const errors = await form.validateForm();
    if (Object.keys(errors).length) {
      form.setErrors(errors);
      const touched = Object.keys(errors).reduce(
        (acc, key) => {
          acc[key as RefundFormField] = true;
          return acc;
        },
        {} as Record<RefundFormField, boolean>,
      );
      form.setTouched(touched as any); // Complains about items array object
      return;
    }

    toggleConfirmationModal();
  };

  const toggleCustomItemForm = () => {
    setIsAddingCustomItem(!isAddingCustomItem);
  };

  const onAddCustomItem = (item: ItemOfOrder) => {
    setCustomItems((prevItems) => [...prevItems, item]);
    setIsAddingCustomItem(false);
    onItemToggle(item)();
  };

  const getFieldError = (field: RefundFormField): string | undefined => {
    if (!form.errors[field]) {
      return undefined;
    }

    if (!form.touched[field]) {
      return undefined;
    }

    return form.errors[field] as string | undefined;
  };

  const isEmailError = !isEmailEditable && !order.customer.email;

  const getCustomerEmailField = (isEditable: boolean) => {
    if (!isEditable) {
      return order.customer.email ? (
        <Value>{order.customer.email}</Value>
      ) : (
        <ErrorMessage visible>{t(LABELS.MESSAGES.EMAIL_ERROR_MESSAGE)}</ErrorMessage>
      );
    }

    return (
      <FormField error={getFieldError(KEYS.FORM_FIELDS.EMAIL)}>
        <Input
          placeholder={t(LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.EMAIL])}
          value={form.values[KEYS.FORM_FIELDS.EMAIL]}
          onChange={onFormChange(KEYS.FORM_FIELDS.EMAIL)}
        />
      </FormField>
    );
  };

  const getSelectableItems = () => {
    if (!order.general.items && !customItems.length) {
      return [];
    }

    const items = (order.general.items ?? []).concat(customItems).map((item) => (
      <ItemButton flat key={item.uuid} onClick={onItemToggle(item)}>
        <Value>
          <Checkbox value={isItemSelected(item.uuid)} />
          <OrderItem key={item.uuid} {...item} currency={order.general.fiatCurrencyCode!} />
        </Value>
      </ItemButton>
    ));

    return items;
  };

  const toggleAdvancedSettings = () => {
    setIsAdvancedSettingsExpanded(!isAdvancedSettingsExpanded);
  };

  const renderSelectedDurationItem = (item: SelectItem) => (
    <SelectedSelectableItem key={item.key}>{t(LABELS.DURATION[item.key as AvailableRefundDuration])}</SelectedSelectableItem>
  );

  const renderAvailableDurationItem = (item: SelectItem, selectItem: (event: MouseEvent<HTMLElement>) => void) => (
    <SelectableItem onClick={selectItem} key={item.key}>
      {t(LABELS.DURATION[item.key as AvailableRefundDuration])}
    </SelectableItem>
  );

  const onInsufficientBalanceGoBack = () => {
    setIsInsuficcientBalance(false);
    setIsConfirming(false);
    setInsufficientBalanceMessage(undefined);
    reset();
  };

  const onInsufficientBalanceProceed = () => {
    setIsConfirming(true);
    setIsInsuficcientBalance(false);
    reset();
  };

  const getContent = () => {
    if (!order.general.fiatCurrencyCode) {
      throw new Error('Order does not have a fiat currency code');
    }

    if (isConfirming) {
      return (
        <ConfirmationDialog
          fiatAmount={Number(form.values[KEYS.FORM_FIELDS.FIAT_AMOUNT] ?? 0)}
          currencyCode={order.general.fiatCurrencyCode}
          settlementAmount={calculateRefundSettlementEstimate(
            form.values[KEYS.FORM_FIELDS.FIAT_AMOUNT],
            estimateAvailability.displayFxRate,
          )}
          settlementCurrencyCode={refundReserve?.settlementCurrencyCode}
          displayFxRate={estimateAvailability.displayFxRate}
          isEstimateLoading={estimateAvailability.isLoading}
          isEstimateUnavailable={estimateAvailability.isUnavailable}
          isLoading={isLoading}
          onCancel={toggleConfirmationModal}
          onProceed={form.submitForm}
        />
      );
    }

    if (isInsufficientBalance) {
      return (
        <>
          <TopupModalContent
            insufficientBalance
            insufficientBalanceMessage={insufficientBalanceMessage}
            merchantUuid={order.merchant.uuid}
          />
          <Delimiter />
          <Buttons>
            <Button secondary onClick={onInsufficientBalanceGoBack}>
              {t(LABELS.ACTIONS.BACK)}
            </Button>
            <Button primary onClick={onInsufficientBalanceProceed}>
              {t(LABELS.ACTIONS.DONE)}
            </Button>
          </Buttons>
        </>
      );
    }

    return (
      <>
        <ModalSection>
          <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.EMAIL])}</Label>
          {getCustomerEmailField(isEmailEditable)}
        </ModalSection>
        <ModalSection>
          <Label>{t(LABELS.SECTIONS.ORDER_DETAILS.ID)}</Label>
          <OutstandingValue>{order.general.uuid}</OutstandingValue>
        </ModalSection>
        <ModalSection>
          <Label>{t(LABELS.SECTIONS.ORDER_DETAILS.REFUNDABLE_AMOUNT)}</Label>
          <Value>
            <span>{formattedSymbol}</span>
            <span>{formattedAmount}</span>
          </Value>
        </ModalSection>
        <Delimiter />
        <ModalSection>
          <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.FIAT_AMOUNT], { currencyCode: formattedSymbol })}</Label>
          <FormField error={getFieldError(KEYS.FORM_FIELDS.FIAT_AMOUNT)}>
            <Input
              startAdornment={<CurrencyAdornment>{formattedSymbol}</CurrencyAdornment>}
              placeholder={formattedPlaceholder}
              type={InputType.Number}
              value={form.values[KEYS.FORM_FIELDS.FIAT_AMOUNT]}
              onChange={onFormChange(KEYS.FORM_FIELDS.FIAT_AMOUNT)}
            />
          </FormField>
        </ModalSection>
        <ModalSection>
          <Label>
            {t(LABELS.FIELDS[KEYS.FORM_FIELDS.ITEMS])}
            <Button flat onClick={toggleAllItems}>
              {toggleAllItemsLabel}
            </Button>
          </Label>
          {getSelectableItems()}
          <OptionallyVisible visible={!isAddingCustomItem}>
            <div>
              <Button flat onClick={toggleCustomItemForm}>
                <IconAdd /> {t(LABELS.ACTIONS.ADD_ITEM)}
              </Button>
            </div>
          </OptionallyVisible>
          <OptionallyVisible visible={isAddingCustomItem}>
            <CustomItemForm fiatCurrencyCode={order.general.fiatCurrencyCode} onAdd={onAddCustomItem} onCancel={toggleCustomItemForm} />
          </OptionallyVisible>
        </ModalSection>

        <Delimiter />

        <ExpandSectionHandle flat onClick={toggleAdvancedSettings}>
          {t(LABELS.SECTIONS.ADVANCED_SETTINGS.TITLE)} <ExpandSectionIcon isExpanded={isAdvancedSettingsExpanded} />
        </ExpandSectionHandle>
        <Collapse in={isAdvancedSettingsExpanded} timeout="auto" unmountOnExit>
          <ModalSection>
            <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.REFUND_TTL])}</Label>
            <FormField error={getFieldError(KEYS.FORM_FIELDS.REFUND_TTL)}>
              <DurationControls>
                <Input
                  placeholder={t(LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.REFUND_TTL])}
                  type={InputType.Number}
                  value={form.values[KEYS.FORM_FIELDS.REFUND_TTL]?.toString()}
                  onChange={onFormChange(KEYS.FORM_FIELDS.REFUND_TTL)}
                />
                <Select
                  fullWidth
                  closeDropdownOnSelect
                  getItemLabel={({ label }) => label}
                  popperProps={{
                    disablePortal: true,
                  }}
                  items={durationSelectItems}
                  value={selectedDuration}
                  renderSelectedItem={renderSelectedDurationItem}
                  renderListItem={renderAvailableDurationItem}
                  onChange={onDurationSelect}
                />
              </DurationControls>
            </FormField>
            <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID])}</Label>
            <FormField error={getFieldError(KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID)}>
              <Input
                placeholder={t(LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID])}
                value={form.values[KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID]}
                onChange={onFormChange(KEYS.FORM_FIELDS.EXTERNAL_REFUND_ID)}
              />
            </FormField>
            <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.REDIRECT_URL])}</Label>
            <FormField error={getFieldError(KEYS.FORM_FIELDS.REDIRECT_URL)}>
              <Input
                placeholder={t(LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.REDIRECT_URL])}
                value={form.values[KEYS.FORM_FIELDS.REDIRECT_URL]}
                onChange={onFormChange(KEYS.FORM_FIELDS.REDIRECT_URL)}
              />
            </FormField>
            <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL])}</Label>
            <FormField error={getFieldError(KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL)}>
              <Input
                placeholder={t(LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL])}
                value={form.values[KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL]}
                onChange={onFormChange(KEYS.FORM_FIELDS.CANCEL_REDIRECT_URL)}
              />
            </FormField>
            <Label>{t(LABELS.FIELDS[KEYS.FORM_FIELDS.WEBHOOK_URL])}</Label>
            <FormField error={getFieldError(KEYS.FORM_FIELDS.WEBHOOK_URL)}>
              <Input
                placeholder={PLAIN_LABELS.PLACEHOLDERS[KEYS.FORM_FIELDS.WEBHOOK_URL]}
                value={form.values[KEYS.FORM_FIELDS.WEBHOOK_URL]}
                onChange={onFormChange(KEYS.FORM_FIELDS.WEBHOOK_URL)}
              />
            </FormField>
          </ModalSection>
        </Collapse>

        <Delimiter />

        <OptionallyVisible visible={Boolean(error)}>
          <ErrorMessage visible>{(error as ReduxToolkitError)?.data?.errorMessage || t(LABELS.ERRORS.GENERIC)}</ErrorMessage>
        </OptionallyVisible>

        <Buttons>
          <Button secondary onClick={toggle}>
            {t(LABELS.ACTIONS.CANCEL)}
          </Button>
          <Button primary onClick={onProceedClick} disabled={!form.isValid || isEmailError || estimateAvailability.isLoading}>
            {t(LABELS.ACTIONS.REFUND)}
          </Button>
        </Buttons>
      </>
    );
  };

  return (
    <StyledDialog open={isOpen} onClose={toggle} scroll="body">
      <Panel
        label={<PanelTitle>{t(LABELS.MODAL_TITLE)}</PanelTitle>}
        controls={
          <CloseButton flat onClick={toggle}>
            <IconCross />
          </CloseButton>
        }
      >
        <ModalContent>{getContent()}</ModalContent>
      </Panel>
    </StyledDialog>
  );
};

export default RefundModal;
